import './index.css'

import { RxInfoCircled } from '@react-icons/all-files/rx/RxInfoCircled'
import without from 'lodash/without'
import React, { useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'

import { WATERMARKING } from '@disco/constants/FeatureTypes'
import {
  clientLibraryContentLoad,
  clientLibraryContentUpdate,
} from '@disco/state-management/actions/client-library-content-actions'
import { useFeatureEnabled } from '@disco/state-management/selectors/business'
import {
  getClientLibrary,
  getClientLibraryGroups,
} from '@disco/state-management/selectors/client-libraries'
import { getUser } from '@disco/state-management/selectors/user'
import type { ClientLibrary, ClientLibraryId } from 'types/client-library'
import type { Group, GroupId } from 'types/group'
import type { State } from 'types/redux'

import DocumentHeadTags from 'components/document-head-tags'
import { Callout, Flex, Link as RadixLink } from 'components/ds'
import Button from 'components/ui/button'
import ButtonContainer from 'components/ui/button-container'
import Checkbox from 'components/ui/checkbox'
import DataTable from 'components/ui/data-table'
import InlineLink from 'components/ui/inline-link'
import Link from 'components/ui/link'
import PageContainer from 'components/ui/page-container'
import TextOverflow from 'components/ui/text-overflow'
import TreeItem from 'components/ui/tree-item'

import { useDispatch, useSelector } from 'hooks/use-redux'

import { getCatalogConnectChannels } from './filter-channels'

type SelectedGroups = Record<GroupId, 'checked' | 'partial'>
type TreeGroup = Group & {
  children?: GroupId[]
  parents: GroupId[]
}

const expandFolders = (
  id: GroupId,
  name: Group['name'],
  tree: Record<GroupId, TreeGroup>,
  expandedGroups: GroupId[],
) => {
  let result = []

  if (tree[id].children) {
    tree[id].children.forEach((child: GroupId) => {
      if (tree[child].parent === id) {
        result.push({ ...tree[child], folderName: name })

        if (expandedGroups.includes(child)) {
          result.push(...expandFolders(child, name, tree, expandedGroups))
        }
      }
    })
  }

  return result
}

const getSelectedGroups = (
  groups: GroupId[],
  tree: Record<GroupId, TreeGroup>,
): SelectedGroups =>
  groups.reduce((accumulator: SelectedGroups, current: GroupId) => {
    let result = accumulator
    result[current] = 'checked'

    if (tree[current].parents) {
      tree[current].parents.forEach((parent: GroupId) => {
        if (result[parent] !== 'checked') {
          result[parent] = 'partial'
        }
      })
    }

    return result
  }, {})

const LibrariesLibraryContent = (): React.ReactNode => {
  const params = useParams<{ libraryId: string }>()
  const libraryId: ClientLibraryId = parseInt(params.libraryId!, 10)
  const dispatch = useDispatch()
  const watermarkingEnabled = useFeatureEnabled(WATERMARKING)
  const library: ClientLibrary | undefined = useSelector((state: State) =>
    getClientLibrary(state, libraryId),
  )
  const user = useSelector(getUser)
  const { client_libraries_allow_multiple_for_discovery } = user.business || {}
  useEffect(() => {
    dispatch(clientLibraryContentLoad(libraryId))
  }, [dispatch, libraryId])
  const [libraryContent, setLibraryContent] = useState<GroupId[]>([])
  const [expandedGroups, setExpandedGroups] = useState<GroupId[]>([])
  const [selectedGroups, setSelectedGroups] = useState<SelectedGroups>({})
  const addedIds: GroupId[] = libraryContent.filter(
    id => library['content'] && !library['content'].includes(id),
  )
  const removedIds: GroupId[] =
    library && library['content']
      ? library['content'].filter(id => !libraryContent.includes(id))
      : []
  const [isFormDisabled, setIsFormDisabled] = useState<boolean>(true)
  useEffect(() => {
    setIsFormDisabled([...addedIds, ...removedIds].length === 0)
  }, [addedIds, removedIds])
  const {
    loading,
    items,
    error: groupsError,
  } = useSelector(getClientLibraryGroups)
  const customChannels: Group[] = useMemo(
    () => getCatalogConnectChannels(items as Record<number, Group>),
    [items],
  )
  const tree = useMemo(
    () =>
      customChannels.reduce(
        (accumulator: Record<GroupId, TreeGroup>, group: Group) => {
          let result = accumulator
          let current = group
          let parents = []

          if (group.parent) {
            while (current) {
              current = customChannels.find(
                group => group.id === current?.parent,
              )

              if (current) {
                parents.push(current.id)
              }
            }

            parents.forEach(id => {
              if (result[id] && result[id].children) {
                result[id].children.push(group.id)
              } else {
                result[id] = { ...result[id], children: [group.id] }
              }
            })
          }

          result[group.id] = { ...result[group.id], ...group, parents }
          return result
        },
        {},
      ),
    [customChannels],
  )
  const channels = useMemo(
    () =>
      (Object.values(tree) as TreeGroup[])
        .filter((group: TreeGroup) => !group.parent)
        .reduce((accumulator: TreeGroup[], current: TreeGroup) => {
          let result = accumulator
          result.push(current)

          if (expandedGroups.includes(current.id)) {
            result.push(
              ...expandFolders(current.id, current.name, tree, expandedGroups),
            )
          }

          return result
        }, []),
    [expandedGroups, tree],
  )
  useEffect(() => {
    if (library && library['content']) {
      setLibraryContent(library['content'])
      setSelectedGroups(getSelectedGroups(library['content'], tree))
    }
  }, [library, tree])

  const toggleGroup = (id: GroupId) => {
    if (tree[id].children) {
      if (expandedGroups.includes(id)) {
        setExpandedGroups(expandedGroups.filter((item: GroupId) => item !== id))
      } else {
        setExpandedGroups([...expandedGroups, id])
      }
    }
  }

  const toggleSelectedGroup = (group: TreeGroup) => {
    const isSelected = (id: GroupId) => libraryContent.includes(id)

    let groups

    if (isSelected(group.id)) {
      groups = without(libraryContent, group.id)

      if (group.children && group.children.every(isSelected)) {
        groups = without(groups, ...group.children)
      }
    } else {
      groups = [...libraryContent, group.id]

      if (group.children && !group.children.some(isSelected)) {
        groups = [...groups, ...group.children]
      }
    }

    setLibraryContent(groups)
    setSelectedGroups(getSelectedGroups(groups, tree))
  }

  const handleSubmit = (event: React.SyntheticEvent<any>) => {
    event.preventDefault()
    dispatch(clientLibraryContentUpdate(libraryId, addedIds, removedIds))
  }

  return (
    <PageContainer.Section
      header={
        <Flex direction="column" py="2" px="3" width="100%" gap="3">
          {library?.discovery_access_level === 'discovery_private' &&
            (client_libraries_allow_multiple_for_discovery ||
              library?.primary) && (
              <Callout.Root color="blue">
                <Callout.Icon>
                  <RxInfoCircled />
                </Callout.Icon>
                <Callout.Text>
                  Update settings in{' '}
                  <RadixLink
                    asChild
                    href={`/catalogs/${libraryId}/access`}
                    underline="none"
                  >
                    <Link>Access & Promotion</Link>
                  </RadixLink>{' '}
                  to allow supervisors to discover music from your catalog using
                  MultiDISCO Search
                </Callout.Text>
              </Callout.Root>
            )}
        </Flex>
      }
      title="Published content"
      description={
        <>
          Here you can set the content for your Catalog. Choose tracks from
          existing channels or folders in your DISCO. Adding channels and
          folders here will pull in the tracks from these channels and folders
          and publish them on your Catalog.
          <br />
          <br />
          Please note this won’t publish the playlists in those channels and
          folders – the playlists you want to feature on your Catalog can be
          selected separately{' '}
          <InlineLink to={`/catalogs/${libraryId}/playlists`}>here</InlineLink>.
          {watermarkingEnabled ? (
            <strong>
              <br />
              <br />
              Please note watermarking is not yet supported within Catalogs. To
              share watermarked content, please use regular playlist share
              functions.
            </strong>
          ) : null}
        </>
      }
    >
      {/* TODO: Add a condition and [Playlist name]
      after completing the task https://app.asana.com/0/1194758472754381/1200389305668011/f*/}
      {/* <div className="LibraryContent_Alert">
       <Alert status="warning" icon="warning">
         This Catalog contains a watermarked playlist: [Playlist name]. Please
         note
         <br /> watermarking is not fully supported within Libraries.{' '}
         <Link className="inline-link" to="watermarking">
           Find out more.
         </Link>
       </Alert>
      </div> */}
      {library && !library['loading'] && (
        <DocumentHeadTags
          title={`${library['name']} | Published content | DISCO`}
        />
      )}
      <DataTable
        appearance="plain"
        columns={[
          {
            name: 'Channel',
            contentType: 'button',
            toSortable: (prop: Record<string, any>) =>
              prop.parent ? prop.folderName : prop.name,
            render: (group: TreeGroup) => (
              <TreeItem
                caption={<TextOverflow content={group.name} />}
                depth={group.parents.length}
                expandable={!!tree[group.id]?.children}
                expanded={expandedGroups.includes(group.id)}
                icon="folder"
                onExpand={() => toggleGroup(group.id)}
              />
            ),
          },
          {
            contentType: 'button',
            name: 'Select',
            width: 'content',
            render: (group: TreeGroup) => (
              <Checkbox
                checked={Boolean(selectedGroups[group.id])}
                partial={selectedGroups[group.id] === 'partial'}
                onChange={() => toggleSelectedGroup(group)}
              />
            ),
          },
        ]}
        data={channels}
        defaultSort={{
          col: 0,
          dir: 1,
        }}
        loading={loading}
        rows={10}
        emptyState={
          groupsError
            ? 'Could not load channels. Refresh and try again.'
            : 'No channels here yet!'
        }
      />
      <PageContainer.Footer inline>
        <ButtonContainer>
          <Button
            disabled={isFormDisabled}
            appearance="primary"
            caption="Update content"
            onClick={handleSubmit}
          />
        </ButtonContainer>
      </PageContainer.Footer>
    </PageContainer.Section>
  )
}

export default LibrariesLibraryContent
